import "@/App.css";
import React, { useEffect, useMemo, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";
import { reportNavLeave, recordDiagEvent, initRefereeNavDiag } from "./lib/refereeNavDiag";
import { initServerBusyNotice } from "./lib/serverBusyNotice";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster, toast } from "sonner";
import { ConfirmDialogProvider } from "./components/ui/ConfirmDialog";

// Phase 83z.73 (Batch 2) — Code splitting via React.lazy. Eager imports below
// only for pages reachable on first paint of any role (Login, Register, root
// redirect targets). All other 60+ pages load via dynamic chunks → athletes
// no longer download admin/referee/wizard code on first load.
//
// Eager (must render immediately):
import NotFound from "./pages/NotFound";
import AuthCallback from "./pages/AuthCallback";
import Login from "./pages/Login";
import Register from "./pages/Register";
// v16.60.15 (Feb 2026) — Apple 1.2 UGC: /terms + /community-guidelines đã có
// route hiện tại (`TermsOfUse`, `CommunityGuidelinesPage` trong /legal/).
// Không cần import trùng — Register.jsx đã link tới các URL đó qua React Router.
// Phase 84.M.101 — Password recovery pages.
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
// v16.98 bundle-audit — legal/support/messages pages chuyển sang lazy chunk
// (không cần cho first paint; Emergent CF pass-through không cache static
// → main.js càng nhỏ càng tốt). Routes đã nằm trong <Suspense> sẵn.
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/legal/PrivacyPolicyPage"));
const SecurityPolicyPage = lazy(() => import("./pages/legal/SecurityPolicyPage"));
const RefundPolicyPage = lazy(() => import("./pages/legal/RefundPolicyPage"));
const CommunityGuidelinesPage = lazy(() => import("./pages/legal/CommunityGuidelinesPage"));
const AccountDeletionPolicyPage = lazy(() => import("./pages/legal/AccountDeletionPolicyPage"));
const ImageLivestreamPolicyPage = lazy(() => import("./pages/legal/ImageLivestreamPolicyPage"));
const AboutAppPage = lazy(() => import("./pages/legal/AboutAppPage"));
const AccountSettingsPage = lazy(() => import("./pages/athlete/AccountSettingsPage"));
const SupportListPage = lazy(() => import("./pages/athlete/SupportListPage"));
const SupportNewPage = lazy(() => import("./pages/athlete/SupportNewPage"));
const AthleteMessagesPage = lazy(() => import("./pages/athlete/AthleteMessagesPage"));
const AdminSupportKnowledgePage = lazy(() => import("./pages/admin/AdminSupportKnowledgePage"));
const SupportThreadPage = lazy(() => import("./pages/athlete/SupportThreadPage"));
const AdminSupportTicketsPage = lazy(() => import("./pages/admin/AdminSupportTicketsPage"));
const AdminSupportThreadPage = lazy(() => import("./pages/admin/AdminSupportThreadPage"));
// Phase 85.A.2 — Legacy redirect components `/athlete/profile*` → `/u/*`.
import {
  LegacyMyProfileRedirect,
  LegacyProfileRedirect,
} from "./pages/redirects/LegacyProfileRedirect";
import Forbidden from "./pages/Forbidden";
import AthleteHomePage from "./pages/AthleteHomePage"; // used by RootRedirect
import { userWorkspaces } from "./lib/adminMenuCatalog";
import SeoHead from "./components/SeoHead";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ClipJobsFloat from "./components/ClipJobsFloat";
import usePublisherBatteryMonitor from "./hooks/usePublisherBatteryMonitor";
import useAdminBatteryToastListener from "./hooks/useAdminBatteryToastListener";
// Phase 84.M.82 — Modal nhắc xác thực điểm trình + re-show 8h.
import RatingVerifyReminderGate from "./components/RatingVerifyReminderGate";
import AdminProposalRequiredModal from "./components/athlete/AdminProposalRequiredModal";
import usePendingPaymentResume from "./hooks/usePendingPaymentResume";
import useVisitorTracker from "./hooks/useVisitorTracker";
import { ThemeProvider } from "./contexts/ThemeContext";
import { I18nProvider } from "./contexts/I18nContext";
import { GuestLoginGateProvider } from "./contexts/GuestLoginGateContext";
// Phase 84.M.85 — Rating gate (chặn đăng ký giải khi chưa xác thực điểm trình).
import { RatingGateProvider } from "./contexts/RatingGateContext";
import { AvatarGateProvider } from "./contexts/AvatarGateContext";
// Phase 84.M.86 — In-app browser detection (Zalo/Messenger/FB/IG/TikTok).
import { InAppBrowserProvider } from "./contexts/InAppBrowserContext";
import UnpaidRegistrationBanner from "./components/payment/UnpaidRegistrationBanner";
import AppUpdateBanner from "./components/common/AppUpdateBanner";
import OfflineBanner from "./components/common/OfflineBanner";
import { scheduleOfflineRoutePreload } from "./lib/offlineRoutePreload";
import AppUpdateGate from "./components/common/AppUpdateGate";
import ChunkErrorBoundary from "./components/common/ChunkErrorBoundary";
// Phase 84.M.93 — Bắt buộc xác thực SĐT cho user đăng nhập qua Google.
import { PhoneVerifyGateProvider } from "./contexts/PhoneVerifyGateContext";
import { AvatarProposalGateProvider } from "./contexts/AvatarProposalGateContext";
// Phase 85.A.121 — Admin yêu cầu VĐV nộp video chấm điểm trình.
import { RatingVideoProposalGateProvider } from "./contexts/RatingVideoProposalGateContext";
// Phase 84.M.94 — Force gate khi VĐV nhận đề xuất điểm trình từ Admin.
import { PendingRatingProposalGateProvider } from "./contexts/PendingRatingProposalGateContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { applyFont, getSavedFont } from "./components/FontSwitcher";
import ErrorReporterFab from "./components/ErrorReporterFab";
// Phase 90.B.fix.workspace-personal — AdminOverlayBar đã bỏ khỏi App.js.
// Workspace switcher giờ render trong tab "Cá nhân" qua WorkspaceSwitcherSection.
import RefereeSyncBootstrap from "./components/referee/RefereeSyncBootstrap";
import LegacyLiveMatchRedirect from "./components/LegacyLiveMatchRedirect";
// Phase 90.C.1 — Referee ↔ Admin voice call (LiveKit Cloud).
import { CallProvider } from "./contexts/CallContext";
import CallModal from "./components/calls/CallModal";
import {
  attachToastInterceptor,
  attachWindowErrorListeners,
} from "./lib/errorReporter";

// Lazy chunks — Admin (bundled together).
const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminBrandingPage = lazy(() => import("./pages/admin/AdminBrandingPage"));
const KarinoxSettingsPage = lazy(() => import("./pages/admin/KarinoxSettingsPage"));
const TelegramWebhookPage = lazy(() => import("./pages/admin/TelegramWebhookPage"));
// Phase 91 — Push thông báo App Mobile + force update config.
const AppNotificationsPage = lazy(() => import("./pages/admin/AppNotificationsPage"));
const BroadcastDetailPage = lazy(() => import("./pages/BroadcastDetailPage"));
const AdminContentReportsPage = lazy(() => import("./pages/admin/AdminContentReportsPage"));
const KarinoxDocsPage = lazy(() => import("./pages/KarinoxDocsPage"));
const DevVodTestPage = lazy(() => import("./pages/DevVodTestPage"));
const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const DuplicateUsersPage = lazy(() => import("./pages/admin/DuplicateUsersPage"));
const PermissionsMatrixPage = lazy(() => import("./pages/admin/PermissionsMatrixPage"));
// Phase 85.A.123 — Permissions Manager v2 (thay thế PermissionsMatrixPage).
const PermissionsManagerPage = lazy(() => import("./pages/admin/PermissionsManagerPage"));
// Phase 85.A.245 — ElevenLabs Voice / TTS admin page.
const VoiceTTSPage = lazy(() => import("./pages/admin/VoiceTTSPage"));
const HLSJobsDashboard = lazy(() => import("./pages/admin/HLSJobsDashboard"));
const MigrationV1751Page = lazy(() => import("./pages/admin/system/MigrationV1751Page"));
const MigrationV1755Page = lazy(() => import("./pages/admin/system/MigrationV1755Page"));
const VenuesPage = lazy(() => import("./pages/admin/VenuesPage"));
const TournamentsListPage = lazy(() => import("./pages/admin/TournamentsListPage"));
const TournamentDetailPage = lazy(() => import("./pages/admin/TournamentDetailPage"));
const TournamentCreateWizard = lazy(() => import("./pages/admin/TournamentCreateWizard"));
const EmailLogsPage = lazy(() => import("./pages/admin/EmailLogsPage"));
const RefactorStatusPage = lazy(() => import("./pages/admin/RefactorStatusPage"));
const BackupsPage = lazy(() => import("./pages/admin/BackupsPage"));
const SecurityPage = lazy(() => import("./pages/admin/SecurityPage"));
// Sprint 2 (02-2026): CacheManagerPage đã gộp vào /admin/cloudflare?tab=cache;
// route /admin/cache redirect qua đó (App.js sử dụng Navigate, không lazy load nữa).
const CloudflarePage = lazy(() => import("./pages/admin/CloudflarePage"));
const LiveScreenPage = lazy(() => import("./pages/admin/LiveScreenPage"));
const AdminLogsPage = lazy(() => import("./pages/admin/AdminLogsPage"));
const AdminMessagesHubPage = lazy(() => import("./pages/admin/AdminMessagesHubPage"));
const ChatboxManagerPage = lazy(() => import("./pages/admin/ChatboxManagerPage"));
const LivestreamManagerPage = lazy(() => import("./pages/admin/LivestreamManagerPage"));
const LedBroadcastPage = lazy(() => import("./pages/admin/LedBroadcastPage"));
const EloManagerPage = lazy(() => import("./pages/admin/EloManagerPage"));
const ErrorReportsPage = lazy(() => import("./pages/admin/ErrorReportsPage"));
const PaymentTemplatePage = lazy(() => import("./pages/admin/PaymentTemplatePage"));
const MatchHealthMonitorPage = lazy(() => import("./pages/admin/MatchHealthMonitorPage"));
const RefereeDiagnosticsPage = lazy(() => import("./pages/admin/RefereeDiagnosticsPage"));

// Lazy chunks — Referee.
const RefereeDashboard = lazy(() => import("./pages/referee/RefereeDashboard"));
const RefereeMatchControl = lazy(() => import("./pages/referee/RefereeMatchControl"));
const RefereeTournamentPage = lazy(() => import("./pages/referee/RefereeTournamentPage"));
const RefereeProfile = lazy(() => import("./pages/referee/RefereeProfile"));
const RefereePendingQueuePage = lazy(() => import("./pages/referee/RefereePendingQueuePage"));

// Lazy chunks — Athlete (heavy pages — homepage stays eager).
const AthleteMatchesPage = lazy(() => import("./pages/AthleteMatchesPage"));
const AthleteLivestreamPage = lazy(() => import("./pages/AthleteLivestreamPage"));
const AthleteRegistrationsPage = lazy(() => import("./pages/AthleteRegistrationsPage"));
const AthleteLiveLobbyPage = lazy(() => import("./pages/AthleteLiveLobbyPage"));
const AthleteRankingPage = lazy(() => import("./pages/AthleteRankingPage"));
const AthleteTournamentsPage = lazy(() => import("./pages/AthleteTournamentsPage"));
const AthleteTournamentDetailPage = lazy(() => import("./pages/AthleteTournamentDetailPage"));
const AthleteRegistrationPage = lazy(() => import("./pages/AthleteRegistrationPage"));
const AthleteEventRegistrationsPage = lazy(() => import("./pages/AthleteEventRegistrationsPage"));
const AthleteTournamentBracketPage = lazy(() => import("./pages/AthleteTournamentBracketPage"));
const PublisherLivePage = lazy(() => import("./pages/publisher/PublisherLivePage"));
const PublisherAutoModePage = lazy(() => import("./pages/publisher/PublisherAutoModePage"));
const AthleteMyMatchesPage = lazy(() => import("./pages/AthleteMyMatchesPage"));
const AthleteMatchHistoryPage = lazy(() => import("./pages/AthleteMatchHistoryPage"));
const AthleteUnifiedProfilePage = lazy(() => import("./pages/AthleteUnifiedProfilePage"));
const AthleteProfileTimelinePage = lazy(() => import("./pages/AthleteProfileTimelinePage"));
const AthleteProfilePartnersPage = lazy(() => import("./pages/AthleteProfilePartnersPage"));

// Lazy chunks — Public (anonymous tournament viewing + live).
const PublicView = lazy(() => import("./pages/PublicView"));
// Phase 85.A.157 — Short-link share page for video highlights.
const ShareVideoPage = lazy(() => import("./pages/ShareVideoPage"));
// v16.22 (Feb 2026) — Public share page cho BTC review VĐV cần xác minh.
const PublicRatingReviewPage = lazy(() => import("./pages/PublicRatingReviewPage"));

const InviteLandingPage = lazy(() => import("./pages/InviteLandingPage")); // Phase 84.M.49
const PublicTournamentPage = lazy(() => import("./pages/public/PublicTournamentPage"));
const PublicRegisterPage = lazy(() => import("./pages/public/PublicRegisterPage"));
const MatchLiveScoreboard = lazy(() => import("./pages/public/MatchLiveScoreboard"));
const TournamentGroupStandings = lazy(() => import("./pages/public/TournamentGroupStandings"));
const LiveGroupStandings = lazy(() => import("./pages/public/LiveGroupStandings"));
const LiveStreamPage = lazy(() => import("./pages/live/LiveStreamPage"));
const LiveStreamMultiPage = lazy(() => import("./pages/live/LiveStreamMultiPage"));
const LiveTickerOverlay = lazy(() => import("./pages/live/LiveTickerOverlay"));
const ObsScoreboardOverlay = lazy(() => import("./pages/live/ObsScoreboardOverlay"));
const SelfHostedWatchPage = lazy(() => import("./pages/SelfHostedWatchPage"));
const HypePage = lazy(() => import("./pages/hype/HypePage"));
const HypeShareLinksPage = lazy(() => import("./pages/hype/HypeShareLinksPage"));

// Phase 83z.73 — Suspense fallback (dark, centered spinner).
function PageFallback() {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#0B1220]"
      data-testid="page-suspense-fallback"
    >
      <div className="w-10 h-10 border-2 border-[#FF8A00]/30 border-t-[#FF8A00] rounded-full animate-spin" />
    </div>
  );
}

/** Root redirect — Phase 81k-extra-5: priority đồng nhất với Login:
 *  - super_admin / organizer (BTC) / admin / staff → /admin/dashboard
 *  - referee → /referee
 *  - athlete → /athlete/profile
 *  - guest → /login.
 *  Trước đây organizer landed vào /admin/tournaments → user yêu cầu BTC + role
 *  cao hơn vào /admin/dashboard nhất quán.
 */
function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Phase 84.M.38 — Guest có thể xem trang chủ VĐV. Click vào tính năng cần
  // auth sẽ pop GuestLoginGateModal.
  if (!user) return <AthleteHomePage />;
  // v16.17 Phase C.41 (Feb 2026) — Publisher THUẦN (roles === ["publisher"])
  // với auto-redirect ON → nhảy về /publisher/auto.
  // v16.60.2 (Feb 2026) — Trong APP NATIVE (WebView đã cài bridge
  // `window.__pickleagueNativeApp`): mọi user có role "publisher" đều được
  // auto-redirect (dù có kèm role "athlete" hay khác) và BỎ QUA toggle.
  // v16.60.4 (Feb 2026) — Bật lại snooze (5 phút) — nút Home ở /publisher/auto
  // vẫn về được trang chủ trong 5 phút. Áp dụng cả web + native.
  const roles = user.roles || [];
  const hasPublisher = roles.includes("publisher");
  const isNativeApp = typeof window !== "undefined" && window.__pickleagueNativeApp === true;
  const inSnooze = (() => {
    try {
      const snoozeUntil = parseInt(localStorage.getItem("pl.pub.auto.snoozed_until") || "0", 10);
      return snoozeUntil && Date.now() < snoozeUntil;
    } catch { return false; }
  })();
  if (isNativeApp && hasPublisher && !inSnooze) {
    return <Navigate to="/publisher/auto?resume=1" replace />;
  }
  if (roles.length === 1 && roles[0] === "publisher") {
    let redirect = true;
    try {
      const disabled = localStorage.getItem("pl.publisher.auto_redirect_disabled") === "1";
      redirect = !disabled && !inSnooze;
    } catch { /* noop */ }
    if (redirect) return <Navigate to="/publisher/auto?resume=1" replace />;
  }
  // Phase 85.A.276 — Trang chủ chung. Mặc định (5b) role đặc biệt (admin/BTC/
  // trọng tài/CTV) vào THẲNG workspace chính. CHỈ khi user bấm "Chuyển sang VĐV"
  // (set flag pl_admin_overlay_v1) mới ở lại trang chủ VĐV — lúc đó floating
  // "Khu làm việc" (AdminOverlayBar) giúp chuyển ngược lại các workspace.
  let wantAthleteHome = false;
  try { wantAthleteHome = localStorage.getItem("pl_admin_overlay_v1") === "1"; } catch { /* noop */ }
  // Mặc định chỉ AUTO vào workspace cho role "vận hành" (operator) vì các role
  // này có sẵn nút "Chuyển sang VĐV" để quay lại. CTV (athlete/publisher chỉ
  // được gán quyền theo giải/menu) là VĐV trước tiên → ở lại trang chủ VĐV,
  // dùng floating "Khu làm việc" để vào workspace khi cần.
  const OPERATOR_ROLES = ["super_admin", "organizer", "admin", "staff", "referee"];
  const isOperator = (user.roles || []).some((r) => OPERATOR_ROLES.includes(r));
  if (!wantAthleteHome && isOperator) {
    const ws = userWorkspaces(user);
    if (ws.length > 0) return <Navigate to={ws[0].to} replace />;
  }
  // VĐV thuần / guest / CTV / role đặc biệt đang ở chế độ "xem trang chủ VĐV".
  return <AthleteHomePage />;
}

// Phase 85.A.63 — Wrapper component cho usePendingPaymentResume hook.
// Phải nằm trong AuthProvider + BrowserRouter để dùng useAuth +
// useNavigate. Hook check localStorage marker khi user login, redirect
// về Bước 4 Thanh toán nếu có pending payment chưa hoàn tất.
function PendingPaymentResumer() {
  usePendingPaymentResume();
  return null;
}

// Phase 85.A.88 — Visitor tracking wrapper (login + guest).
// Phải nằm BÊN TRONG BrowserRouter để dùng useLocation.
function VisitorTracker() {
  useVisitorTracker();
  // Đợt Offline-1 — sync route cho Network Governor (Operational Context:
  // referee/publisher/admin → passive mode).
  const location = useLocation();
  useEffect(() => {
    try {
      const gov = require("./lib/networkGovernor");
      gov.setRoute(location.pathname);
    } catch { /* noop */ }
  }, [location.pathname]);
  // PR-K v2 UX — đăng ký 1 lần listener toast "hệ thống bận" (giữ data cũ).
  useEffect(() => {
    initServerBusyNotice();
  }, []);
  return null;
}

// v16.17 Phase C.40 (Feb 2026) — Publisher Battery Monitor + Admin Toast subscriber.
// Mount ở cấp cao nhất trong AuthProvider để hoạt động trên MỌI trang app.
function PublisherBatteryWatcher() {
  const { user } = useAuth();
  usePublisherBatteryMonitor(user);
  useAdminBatteryToastListener(user);
  return null;
}

// v20.9-P — OTA Update Toast: khi native app vừa update (EAS OTA), hiển thị
// toast cho admin / publisher / trọng tài. Flag set bởi bridge:
// window.__pickleagueOtaJustUpdated = true.
function OtaUpdateToaster() {
  const { user } = useAuth();
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    if (!user) return;
    if (typeof window === "undefined" || !window.__pickleagueOtaJustUpdated) return;
    const OTA_TOAST_ROLES = ["super_admin", "organizer", "admin", "staff", "referee", "publisher"];
    const roles = user.roles || [];
    if (!roles.some((r) => OTA_TOAST_ROLES.includes(r))) return;
    firedRef.current = true;
    window.__pickleagueOtaJustUpdated = false;
    const version = window.__pickleagueNativeAppVersion || "";
    toast.success(`App vừa cập nhật${version ? " (v" + version + ")" : ""}`, { duration: 4000 });
  }, [user]);
  return null;
}

// Phase 85.A.319 — Watcher cấp Router: phát hiện RỜI màn chấm điểm
// (/referee/match/:id → route khác) và gửi log chẩn đoán kèm navType
// (POP=back/vuốt · PUSH=nút/Link · REPLACE=route-guard/hệ thống tự điều hướng).
function RefereeNavWatcher() {
  const location = useLocation();
  const navType = useNavigationType();
  const prevRef = useRef(location.pathname);
  useEffect(() => {
    initRefereeNavDiag();
  }, []);
  useEffect(() => {
    const prev = prevRef.current;
    const cur = location.pathname;
    if (prev === cur) return;
    const matchRe = /^\/referee\/match\//;
    const wasMatch = matchRe.test(prev);
    const stillMatch = matchRe.test(cur);
    recordDiagEvent("route_change", { from: prev, to: cur, navType });
    if (wasMatch && !stillMatch) {
      reportNavLeave({ navType, fromRoute: prev, toRoute: cur });
    }
    prevRef.current = cur;
  }, [location.pathname, navType]);
  return null;
}

function App() {
  useEffect(() => {
    applyFont(getSavedFont().stack);
    // Đợt Offline-1 — khởi động Network Governor (nghe bridge native +
    // browser online/offline + visibility).
    try {
      const { initNetworkGovernor } = require("./lib/networkGovernor");
      initNetworkGovernor();
    } catch { /* noop */ }
    // Đợt Offline-3 — preload chunk các route public chính lúc idle (jitter
    // 5-30s) để SPA điều hướng được các trang chính khi offline.
    scheduleOfflineRoutePreload([
      () => import("./pages/AthleteTournamentsPage"),
      () => import("./pages/AthleteTournamentDetailPage"),
      () => import("./pages/AthleteRankingPage"),
      () => import("./pages/AthleteMatchesPage"),
    ]);
    // Phase 83z.23 — wrap sonner toast.error so every error lands in the
    // reporter buffer + catch silent window errors for the FAB to surface.
    attachToastInterceptor();
    attachWindowErrorListeners();
    // Phase 85.A.77 — Prime AudioContext sau user gesture đầu tiên (browser
    // policy chặn autoplay). Sau khi click, audio "ting ting" cho tin
    // nhắn mới sẽ hoạt động.
    const onFirstGesture = () => {
      try {
        // Lazy import để tránh side-effect lúc bundle init.
        import("./lib/messageNotificationSound").then((m) => m.primeAudio?.());
      } catch { /* noop */ }
      window.removeEventListener("click", onFirstGesture);
      window.removeEventListener("keydown", onFirstGesture);
      window.removeEventListener("touchstart", onFirstGesture);
    };
    window.addEventListener("click", onFirstGesture, { once: false });
    window.addEventListener("keydown", onFirstGesture, { once: false });
    window.addEventListener("touchstart", onFirstGesture, { once: false });
    return () => {
      window.removeEventListener("click", onFirstGesture);
      window.removeEventListener("keydown", onFirstGesture);
      window.removeEventListener("touchstart", onFirstGesture);
    };
  }, []);
  // Phase 82a — React Query client (1 instance per app, stable ref)
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // v17.perf.auth-storm (02/2026) — HAR post-login cho thấy 8-10x
            // fetch cùng endpoint (rating-proposals, registrations, branding…)
            // do StrictMode double-mount + đổi route mount lại provider.
            // staleTime dài + refetchOnMount:false → dedupe triệt để.
            staleTime: 2 * 60_000,    // 2 min — dữ liệu vẫn "tươi" giữa các mount
            gcTime: 10 * 60_000,      // 10 min cache retention
            refetchOnWindowFocus: false,
            refetchOnMount: false,    // component mount không tự refetch (đã có cache)
            retry: 1,
          },
        },
      }),
    []
  );
  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <I18nProvider>
            <AuthProvider>
              <InAppBrowserProvider>
              <GuestLoginGateProvider>
              <RatingGateProvider>
              <AvatarGateProvider>
              <PhoneVerifyGateProvider>
              <AvatarProposalGateProvider>
              <RatingVideoProposalGateProvider>
              <PendingRatingProposalGateProvider>
              <CallProvider>
              <SeoHead />
              <UnpaidRegistrationBanner />
              <AppUpdateBanner />
              {/* Đợt Offline-3 — banner trạng thái mạng (ẩn ở context referee/publisher/admin). */}
              <OfflineBanner />
              <AppUpdateGate />
              {/* Phase 83y.31 — Global Sonner toast portal (was missing!
                  Every toast.success/error/info silently failed before
                  this mount). Theme=dark + top-center to overlay
                  cleanly.
                  v16.61.2 (Feb 2026) — Bug iOS Dynamic Island / notch:
                  offset dùng env(safe-area-inset-top) để toast luôn nằm
                  dưới notch + Dynamic Island trên iPhone 14 Pro trở lên,
                  và dưới status bar trên Android có notch. */}
              <Toaster
                theme="dark"
                position="top-center"
                offset="calc(env(safe-area-inset-top, 0px) + 16px)"
                richColors
                closeButton
                toastOptions={{
                  style: {
                    background: "rgba(11,18,32,0.96)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    color: "#fff",
                  },
                }}
              />
              {/* Phase 83y.31 — Global Pickleague-themed confirm dialog
                  portal. Replaces native window.confirm() popups. */}
              <ConfirmDialogProvider />
              {/* Phase 83z.23 — Floating "Báo lỗi cho BTC" button. Only
                  renders when the error buffer has ≥1 entry. */}
              <ErrorReporterFab />
              {/* Phase 90.B.fix.workspace-personal — Floating "Khu làm việc"
                  (AdminOverlayBar) ĐÃ BỎ vì gây vướng giao diện ở mọi trang.
                  Workspace switcher giờ nằm gọn trong tab "Cá nhân"
                  (WorkspaceSwitcherSection ở AthletePersonalPage). */}
              {/* Phase 83z.61 — Referee offline sync engine bootstrap +
                  sticky banner shown only when on /referee/* routes. */}
              <RefereeSyncBootstrap />
              {/* Phase 84.M.82 — Modal nhắc xác thực điểm trình (re-show
                  mỗi 8h cho user chưa verified ở routes /athlete/*). */}
              <RatingVerifyReminderGate />
              {/* Phase 85.A.69 — Modal bắt buộc khi Admin chủ động đề
                  xuất điểm trình mới (admin_initiated=true). Q11=b: KHÔNG
                  tắt được cho đến khi user Accept/Reject. */}
              <AdminProposalRequiredModal />
              {/* Phase 85.A.63 — Auto-resume Bước 4 Thanh toán khi user
                  quay lại app sau khi lỡ tay tắt trình duyệt hoặc crash
                  giữa chừng chuyển khoản. Đọc localStorage marker → check
                  backend → redirect khi cần. */}
              <PendingPaymentResumer />
              {/* Phase 85.A.88 — Visitor traffic tracking (login + guest).
                  Mỗi route change + heartbeat 30s → POST /api/public/visit. */}
              <VisitorTracker />
              {/* Phase 85.A.319 — Chẩn đoán "trọng tài bị văng khỏi màn chấm
                  điểm": ghi log mỗi khi RỜI /referee/match/:id kèm navType. */}
              <RefereeNavWatcher />
              {/* v16.17 Phase C.40 — Publisher battery heartbeat + admin toast. */}
              <PublisherBatteryWatcher />
              {/* v20.9-P — OTA update toast cho admin/publisher/referee. */}
              <OtaUpdateToaster />
              {/* Auto Highlight — thanh float toàn cục theo dõi clip cá nhân
                  đang cắt ngầm: progress + Dừng + Tải về + Thoát; sống qua
                  reload/tắt-mở app cho tới khi bấm Thoát. */}
              <ClipJobsFloat />
              <ChunkErrorBoundary>
              <Suspense fallback={<PageFallback />}>
              <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            {/* Phase 84.M.101 — Password recovery flow. */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Phase 84.M.103 — Legal pages cho App Store / Play Console submission. */}
            {/* v16.60.18 — Apple 5.1.1(v): /privacy redirect về /privacy-policy để
                URL metadata App Store + link in-app trỏ về CÙNG một tài liệu. */}
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/terms" element={<TermsOfUse />} />
            {/* v16.53 — Support page (bắt buộc cho App Store Support URL). */}
            <Route path="/support" element={<SupportPage />} />
            {/* Phase 84.M.108 — Legal pages 2.0 (canonical URLs theo yêu cầu user). */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/security-policy" element={<SecurityPolicyPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/community-guidelines" element={<CommunityGuidelinesPage />} />
            <Route path="/account-deletion-policy" element={<AccountDeletionPolicyPage />} />
            <Route path="/image-livestream-policy" element={<ImageLivestreamPolicyPage />} />
            <Route path="/about-app" element={<AboutAppPage />} />
            {/* Phase 84.M.109 — Account settings + Support tickets. */}
            <Route path="/athlete/settings/account" element={<ProtectedRoute><AccountSettingsPage /></ProtectedRoute>} />
            {/* Phase 91 — Trang chi tiết thông báo admin gửi qua app */}
            <Route path="/broadcasts/:id" element={<ProtectedRoute><BroadcastDetailPage /></ProtectedRoute>} />
            <Route path="/athlete/support" element={<ProtectedRoute><SupportListPage /></ProtectedRoute>} />
            <Route path="/athlete/support/new" element={<ProtectedRoute><SupportNewPage /></ProtectedRoute>} />
            <Route path="/athlete/support/:id" element={<ProtectedRoute><SupportThreadPage /></ProtectedRoute>} />
            {/* Phase 85.A.73 — Messenger-style chat với BTC (1 user ↔ 1 conversation) */}
            <Route path="/athlete/messages" element={<ProtectedRoute><AthleteMessagesPage /></ProtectedRoute>} />
            <Route path="/register" element={<Register />} />
            {/* Phase 85.A.23 — Public Karinox dev integration docs */}
            <Route path="/karinox-docs" element={<KarinoxDocsPage />} />
            <Route path="/dev/vod-test" element={<DevVodTestPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/403" element={<Forbidden />} />
            <Route path="/p/:id" element={<PublicView />} />
            {/* Phase 85.A.157 — Short-link chia sẻ video highlight. */}
            <Route path="/v/:id" element={<ShareVideoPage />} />
            {/* Phase 84.M.49 — Partner invite landing */}
            <Route path="/invite/:invite_id" element={<InviteLandingPage />} />
            {/* v16.22 — Public share page cho BTC chấm trình VĐV chung */}
            <Route path="/rating-review/:token" element={<PublicRatingReviewPage />} />

            {/* Public tournament + registration */}
            <Route path="/t/:slug" element={<PublicTournamentPage />} />
            <Route path="/t/:slug/register/:eventId" element={<PublicRegisterPage />} />
            <Route path="/t/:slug/group-stage" element={<TournamentGroupStandings />} />
            <Route path="/live/match/:id" element={<MatchLiveScoreboard />} />
            <Route path="/live/standings/:eventId" element={<LiveGroupStandings />} />
            {/* Phase 85.A.306 — OBS ticker overlay (đặt trước :eventSlug) */}
            <Route path="/live/:slug/ticker" element={<LiveTickerOverlay />} />
            {/* Phase 85.A.312 — OBS Livescore Overlay (broadcast scoreboard, token public) */}
            <Route path="/obs/scoreboard/:token" element={<ObsScoreboardOverlay />} />
            <Route path="/live/:slug/:eventSlug/ticker" element={<LiveTickerOverlay />} />
            <Route path="/live/:slug/:eventSlug" element={<LiveStreamPage />} />
            <Route path="/live/:slug" element={<LiveStreamMultiPage />} />
            {/* Phase 85.A.199.fe.viewer — Public viewer self-hosted livestream. */}
            <Route path="/watch/self/:sessionId" element={<SelfHostedWatchPage />} />
            <Route path="/g/:slug" element={<HypePage />} />
            <Route path="/g/:slug/preview" element={<HypePage forcePreview={true} />} />
            <Route path="/g/:slug/links" element={<HypeShareLinksPage />} />

            {/* Phase 81bbb — Athlete Matches (new) + Live Score detail
                Phase 84.M.68 — Public access: guest có thể xem live feed.
                Match card click navigates to /athlete/tournaments/:id?tab=livestream
                (vẫn ProtectedRoute → redirect login với returnTo). */}
            <Route
              path="/athlete/matches"
              element={<AthleteMatchesPage />}
            />
            <Route
              path="/athlete/matches/:matchId/live"
              element={
                <ProtectedRoute>
                  <LegacyLiveMatchRedirect />
                </ProtectedRoute>
              }
            />
            {/* Phase 81ccc — Athlete Livestream Page (multi-court switcher) */}
            <Route
              path="/athlete/livestream"
              element={
                <ProtectedRoute>
                  <AthleteLivestreamPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/livestream/:matchId"
              element={
                <ProtectedRoute>
                  <AthleteLivestreamPage />
                </ProtectedRoute>
              }
            />
            {/* Phase 81ddd — My Registrations Dashboard + Live Lobby */}
            <Route
              path="/athlete/registrations"
              element={
                <ProtectedRoute>
                  <AthleteRegistrationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/live"
              element={
                <ProtectedRoute>
                  <AthleteLiveLobbyPage />
                </ProtectedRoute>
              }
            />
            {/* Phase 81eee — Athlete Ranking Page (BXH với Top 3 + analytics + ELO chart + comparison)
                Phase 84.M.68 — Public access: guest xem BXH chung công khai. */}
            <Route
              path="/athlete/ranking"
              element={<AthleteRankingPage />}
            />

            {/* Referee area */}
            <Route
              path="/referee"
              element={
                <ProtectedRoute roles={["super_admin", "organizer", "referee"]}>
                  <RefereeDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/referee/match/:id"
              element={
                <ProtectedRoute roles={["super_admin", "organizer", "referee"]}>
                  <RefereeMatchControl />
                </ProtectedRoute>
              }
            />
            <Route
              path="/referee/tournaments/:tid"
              element={
                <ProtectedRoute roles={["super_admin", "organizer", "referee"]}>
                  <RefereeTournamentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/referee/profile"
              element={
                <ProtectedRoute roles={["super_admin", "organizer", "referee"]}>
                  <RefereeProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/referee/pending"
              element={
                <ProtectedRoute roles={["super_admin", "organizer", "referee"]}>
                  <RefereePendingQueuePage />
                </ProtectedRoute>
              }
            />

            {/* Admin panel — requires auth */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="tournaments" element={<TournamentsListPage />} />
              <Route path="tournaments/create" element={<TournamentCreateWizard />} />
              <Route path="tournaments/:id/edit" element={<TournamentCreateWizard />} />
              <Route path="tournaments/:id" element={<TournamentDetailPage />} />
              {/* Phase 83y.63 — Legacy: now lives inside /admin/led-broadcast.
                  Keep this route for backwards-compatible deep-links from
                  notifications, but the menu link is gone. */}
              <Route
                path="chatbox"
                element={
                  <ProtectedRoute roles={["super_admin", "organizer", "admin"]}>
                    <ChatboxManagerPage />
                  </ProtectedRoute>
                }
              />
              {/* Phase 83z.72 — Unified LED & Broadcast shell */}
              <Route
                path="led-broadcast"
                element={
                  <ProtectedRoute roles={["super_admin", "organizer", "admin"]}>
                    <LedBroadcastPage />
                  </ProtectedRoute>
                }
              />
              {/* Phase 85.A.112 — Cloudflare Stream OBS livestream manager */}
              <Route
                path="livestream"
                element={
                  <ProtectedRoute roles={["super_admin", "organizer", "admin"]}>
                    <LivestreamManagerPage />
                  </ProtectedRoute>
                }
              />
              {/* Phase 85.A.279 — Bảo mật & Chống DDoS (L7 + Cloudflare API) */}
              <Route
                path="security"
                element={
                  <ProtectedRoute roles={["super_admin", "admin"]}>
                    <SecurityPage />
                  </ProtectedRoute>
                }
              />
              {/* 2026-08 → Sprint 2 (02-2026) — Cache Manager giờ là sub-tab
                  của Cloudflare hub. Route cũ redirect để không mất backlink. */}
              <Route
                path="cache"
                element={<Navigate to="/admin/cloudflare?tab=cache" replace />}
              />
              {/* Sprint 2 (02-2026) — Cloudflare IaC (super_admin only) */}
              <Route
                path="cloudflare"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <CloudflarePage />
                  </ProtectedRoute>
                }
              />
              {/* Phase 83z.17 — Quản lý ELO (Tổng quan + Lịch sử) */}
              <Route
                path="elo"
                element={
                  <ProtectedRoute roles={["super_admin", "organizer"]}>
                    <EloManagerPage />
                  </ProtectedRoute>
                }
              />
              {/* Phase 83z.23 — Báo lỗi từ người dùng (triage) */}
              <Route
                path="error-reports"
                element={
                  <ProtectedRoute roles={["super_admin", "organizer", "admin"]}>
                    <ErrorReportsPage />
                  </ProtectedRoute>
                }
              />
              {/* Phase 83z.27 — Quản lý cú pháp nội dung thanh toán */}
              <Route
                path="settings/payment-template"
                element={
                  <ProtectedRoute roles={["super_admin", "organizer", "admin"]}>
                    <PaymentTemplatePage />
                  </ProtectedRoute>
                }
              />
              {/* Phase 83z.53 — Theo dõi sức khỏe trận đấu (Match Health Monitor) */}
              <Route
                path="match-health"
                element={
                  <ProtectedRoute roles={["super_admin", "organizer", "admin"]}>
                    <MatchHealthMonitorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="audit-logs"
                element={
                  <ProtectedRoute roles={["super_admin", "organizer", "admin"]}>
                    <AdminLogsPage />
                  </ProtectedRoute>
                }
              />
              {/* Phase 85.A.319 — Chẩn đoán phiên Trọng tài */}
              <Route
                path="referee-diagnostics"
                element={
                  <ProtectedRoute roles={["super_admin", "organizer", "admin"]}>
                    <RefereeDiagnosticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="users"
                element={
                  <ProtectedRoute roles={["super_admin", "organizer"]}>
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/users"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              {/* Phase 85.A.21 — Admin Support Tickets: nest vào /admin để Outlet hoạt động */}
              <Route path="support-tickets" element={<AdminSupportTicketsPage />} />
              <Route path="support-tickets/:id" element={<AdminSupportThreadPage />} />
              {/* Phase 85.A.73 — Admin Messenger Inbox (1 user ↔ 1 conversation + AI assistant) */}
              {/* Phase 85.A.331 — Gộp thành hub 3 tab: Tin nhắn / Tin nhắn cũ / Knowledge Base */}
              <Route path="messages" element={<AdminMessagesHubPage />} />
              {/* Phase 85.A.74 — Admin Knowledge Base manager (CRUD UI cho AI semi-RAG) */}
              <Route path="messages/knowledge" element={<AdminSupportKnowledgePage />} />
              {/* v16.60.16 — Apple 1.2: Admin UI duyệt content reports */}
              <Route
                path="content-reports"
                element={
                  <ProtectedRoute roles={["super_admin", "admin"]}>
                    <AdminContentReportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/users/duplicates"
                element={
                  <ProtectedRoute roles={["super_admin", "organizer", "admin"]}>
                    <DuplicateUsersPage />
                  </ProtectedRoute>
                }
              />
              {/* Phase 84.M.76 — Auto daily backup management */}
              <Route
                path="settings/backups"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <BackupsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="branding"
                element={
                  <ProtectedRoute roles={["super_admin", "admin"]}>
                    <AdminBrandingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/permissions"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <PermissionsManagerPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/permissions-legacy"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <PermissionsMatrixPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/voice"
                element={
                  <ProtectedRoute roles={["super_admin", "admin", "organizer", "staff"]}>
                    <VoiceTTSPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="system/hls-jobs"
                element={
                  <ProtectedRoute roles={["super_admin", "admin", "organizer"]}>
                    <HLSJobsDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="system/migrations/v17-51"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <MigrationV1751Page />
                  </ProtectedRoute>
                }
              />
              <Route
                path="system/migrations/v17-55"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <MigrationV1755Page />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/venues"
                element={
                  <ProtectedRoute roles={["super_admin", "organizer"]}>
                    <VenuesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/courts"
                element={
                  <ProtectedRoute roles={["super_admin", "organizer"]}>
                    <VenuesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/emails"
                element={
                  <ProtectedRoute roles={["super_admin", "organizer"]}>
                    <EmailLogsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/karinox"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <KarinoxSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/telegram-webhook"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <TelegramWebhookPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/refactor"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <RefactorStatusPage />
                  </ProtectedRoute>
                }
              />
              {/* Phase 91 — Push thông báo App Mobile */}
              <Route
                path="app-notifications"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <AppNotificationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="live-screen"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <LiveScreenPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Phase 81vv — Athlete homepage tại "/" (no Shell — full-bleed mobile-first) */}
            <Route path="/" element={<RootRedirect />} />
            {/* Phase 82h — Direct route for athlete homepage (also accessible at "/" when role=athlete) */}
            {/* Phase 84.M.38 — Bỏ ProtectedRoute: cho phép guest xem trang chủ. */}
            <Route
              path="/athlete/home"
              element={<AthleteHomePage />}
            />

            {/* Phase 81xx — Athlete tournament list page
                Phase 84.M.74 — Mở guest access (xem-only, action gate) */}
            <Route
              path="/athlete/tournaments"
              element={<AthleteTournamentsPage />}
            />
            {/* Phase 81yy — Athlete tournament detail page
                Phase 84.M.74 — Mở guest access read-only: guest xem tất cả
                tab, click action (Đăng ký giải / Vote / Comment) → modal. */}
            <Route
              path="/athlete/tournaments/:id"
              element={<AthleteTournamentDetailPage />}
            />
            {/* v16.17 Phase C.31 — Path-based tab route (SEO-friendly VI slug).
                VD: /athlete/tournaments/pickleball-2026/livestream
                    /athlete/tournaments/pickleball-2026/su-kien */}
            <Route
              path="/athlete/tournaments/:id/:tabSlug"
              element={<AthleteTournamentDetailPage />}
            />
            {/* Phase 81zz — Athlete tournament registration flow (5 steps)
                Vẫn protected: đăng ký giải BẮT BUỘC login. */}
            <Route
              path="/athlete/tournaments/:id/register"
              element={
                <ProtectedRoute>
                  <AthleteRegistrationPage />
                </ProtectedRoute>
              }
            />
            {/* Phase 83b — Athlete Event Registrations List (Danh sách đăng ký theo nội dung)
                Phase 84.M.74 — Open guest (xem danh sách đăng ký công khai). */}
            <Route
              path="/athlete/tournaments/:tid/events/:eid/registrations"
              element={<AthleteEventRegistrationsPage />}
            />
            {/* Phase 83c — Athlete Tournament Bracket (full-screen sơ đồ cây đấu)
                Phase 84.M.74 — Open guest (bracket view-only). */}
            <Route
              path="/athlete/tournaments/:id/bracket"
              element={<AthleteTournamentBracketPage />}
            />
            {/* Phase 89.M1 — Publisher Live Page (PWA Android livestream từ điện thoại) */}
            <Route
              path="/publisher/live/:matchId"
              element={
                <ProtectedRoute>
                  <PublisherLivePage />
                </ProtectedRoute>
              }
            />
            {/* Phase 90.C.livestream-auto — Publisher Auto-Mode (pair court + auto play). */}
            <Route
              path="/publisher/auto"
              element={
                <ProtectedRoute>
                  <PublisherAutoModePage />
                </ProtectedRoute>
              }
            />
            {/* Phase 83e — Athlete My Matches Full List */}
            <Route
              path="/athlete/my-matches"
              element={
                <ProtectedRoute>
                  <AthleteMyMatchesPage />
                </ProtectedRoute>
              }
            />
            {/* Phase 83z.34 — Detailed match history (infinite scroll + filters). */}
            <Route
              path="/athlete/match-history"
              element={
                <ProtectedRoute>
                  <AthleteMatchHistoryPage />
                </ProtectedRoute>
              }
            />
            {/* Phase 81aaa — Athlete Personal Hub + Player Profile Detail
                Phase 84.M.68 — Public access: guest thấy landing chào đăng ký.
                Phase 85.A.2 — GỘP 2 trang vào AthleteUnifiedProfilePage:
                  • Canonical URL: `/u/:identifier` (username hoặc user.id)
                  • Legacy `/athlete/profile*` → 301 redirect sang URL mới
                  • Owner detection: profile.id === me.id (server resolves) */}
            <Route
              path="/u/:identifier"
              element={<AthleteUnifiedProfilePage />}
            />
            <Route
              path="/u/:identifier/timeline"
              element={
                <ProtectedRoute>
                  <AthleteProfileTimelinePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/u/:identifier/partners"
              element={
                <ProtectedRoute>
                  <AthleteProfilePartnersPage />
                </ProtectedRoute>
              }
            />

            {/* ─── Legacy redirects (Phase 85.A.2) ─────────────────── */}
            <Route
              path="/athlete/profile"
              element={<LegacyMyProfileRedirect />}
            />
            <Route
              path="/athlete/profile/:id"
              element={<LegacyProfileRedirect suffix="" />}
            />
            <Route
              path="/athlete/profile/:athleteId/timeline"
              element={
                <ProtectedRoute>
                  <LegacyProfileRedirect suffix="/timeline" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/profile/:athleteId/partners"
              element={
                <ProtectedRoute>
                  <LegacyProfileRedirect suffix="/partners" />
                </ProtectedRoute>
              }
            />

            {/* Phase 83y.26 — Catch-all 404. Replaces the legacy
                Bracket.Ops Shell fallback (Dashboard/NewTournament/
                TournamentDetail UI) which sometimes flashed for unknown
                URLs and confused users. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
              </Suspense>
              </ChunkErrorBoundary>
              <CallModal />
              </CallProvider>
              </PendingRatingProposalGateProvider>
              </RatingVideoProposalGateProvider>
              </AvatarProposalGateProvider>
              </PhoneVerifyGateProvider>
              </AvatarGateProvider>
              </RatingGateProvider>
              </GuestLoginGateProvider>
              </InAppBrowserProvider>
            </AuthProvider>
          </I18nProvider>
        </ThemeProvider>
      </BrowserRouter>
      </QueryClientProvider>
    </div>
  );
}

export default App;
